import { gql } from '@apollo/client';
import {
  EmptyState,
  Flag,
  isFlagCode,
  Link,
  Page,
  PageHeader,
  ResourceList,
  Select,
  Symbols,
} from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import AbsoluteRedirect from '../../components/AbsoluteRedirect';
import { useSourcesQuery } from '../../generated/hooks';
import mndUrl, { SubDomain } from '../../lib/mndUrl';
import { supportEmail } from '../../lib/mynewsdeskEmails';
import qs from '../../lib/normalizingQs';
import QuickNavigate from '../../modules/global/components/DebugInfo/QuickNavigate';

const Action = styled.div`
  margin-top: 2rem;
  text-align: left;
`;

gql`
  query Sources {
    me {
      id
      user {
        id
        isMynewsdeskAdmin
        sources {
          id
          name
          pressrooms {
            id
            name
            siteCode
          }
        }
      }
    }
  }
`;

const PressroomSelectorPage = () => {
  const { search } = useLocation();
  const query = qs.parse(search);
  const redirectTo = query.redirect ? query.redirect : '/dashboard';

  const [t] = useTranslation();
  const { showFailureToast } = useToasts();
  const [selectedSourceId, setSelectedSourceId] = useState<undefined | string>(undefined);
  const { data, loading, error } = useSourcesQuery();

  const sources = useMemo(() => data?.me?.user?.sources ?? [], [data]);

  const toastErrorMessage = error && t('common_general_error');
  useEffect(() => {
    if (toastErrorMessage) {
      showFailureToast(toastErrorMessage);
    }
  }, [toastErrorMessage, showFailureToast]);

  const isSinglePressroom = sources.length === 1 && sources[0].pressrooms.length === 1;

  useEffect(() => {
    if (sources.length > 0) {
      setSelectedSourceId(sources[0].id);
    }
  }, [sources]);

  const sourceOptions = sources.map(source => ({
    label: source.name ?? '',
    value: source.id,
  }));

  const source = sources.find(source => source.id === selectedSourceId);

  const isDropdownVisible = sources.length > 1;

  const isAdmin = data?.me?.user?.isMynewsdeskAdmin === true;
  if (isAdmin) {
    return (
      <Page>
        <PageHeader title={t('pressroom_selector_title')} />
        <QuickNavigate redirectTo={redirectTo} />
      </Page>
    );
  }

  if (isSinglePressroom) {
    return (
      <AbsoluteRedirect
        href={mndUrl(SubDomain.Publish, `/${sources[0].pressrooms[0].id}${redirectTo}`)}
      />
    );
  }

  return (
    <Page>
      <PageHeader title={t('pressroom_selector_title')}>
        <Action>
          {isDropdownVisible ? (
            <Select
              label={t('common_newsroom')}
              options={sourceOptions}
              onChange={o => o && setSelectedSourceId(o)}
              value={selectedSourceId}
            />
          ) : undefined}
        </Action>
      </PageHeader>
      <ResourceList
        isLoading={loading}
        empty={
          <EmptyState
            symbol={error ? Symbols.BrokenLink : Symbols.Newspaper}
            headline={t('pressroom_selector_empty')}
            description={
              error ? (
                <>
                  <div>{t('common_general_error')}</div>
                  <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                </>
              ) : source ? (
                <Trans
                  i18nKey="pressroom_selector_empty_source_but_no_newsrooms"
                  components={{
                    // biome-ignore lint: passed to Trans
                    support_link: <a href={`mailto:${supportEmail}`} />,
                  }}
                />
              ) : undefined
            }
          />
        }
        items={source?.pressrooms.map(pressroom => {
          const siteCode = pressroom.siteCode.replace('_', '-');
          return {
            title: (
              <Link
                data-testid={`pressroom-site-${pressroom.siteCode}`}
                href={mndUrl(SubDomain.Publish, `/${pressroom.id}${redirectTo}`)}
              >
                {pressroom.name}
              </Link>
            ),
            avatar: <Flag code={isFlagCode(siteCode) ? siteCode : undefined} size="2rem" />,
          };
        })}
      />
    </Page>
  );
};

export default PressroomSelectorPage;
